/**
 * Bilingual text helpers.
 *
 * Editable texts in this app are stored as objects of the shape `{ es, en }`.
 * Legacy plain strings are still tolerated to keep older data working without
 * a forced migration.
 */

export const LANG_FLAGS = { es: "🇪🇸", en: "🇺🇸" };
export const LANG_LABELS = { es: "Español", en: "English" };

/**
 * Pick the best text for the current language, falling back gracefully.
 *
 *  pickText("hola", "en")               -> "hola"           (legacy string)
 *  pickText({es:"hola",en:"hi"}, "en")  -> "hi"
 *  pickText({es:"hola",en:""}, "en")    -> "hola"           (empty EN -> ES fallback)
 *  pickText({es:"",en:"hi"}, "es")      -> "hi"
 *  pickText(null, "en")                 -> ""
 */
export function pickText(value, lang) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value !== "object") return "";
  const target = (value[lang] ?? "").toString().trim();
  if (target) return value[lang];
  const other = lang === "es" ? "en" : "es";
  const otherVal = (value[other] ?? "").toString().trim();
  if (otherVal) return value[other];
  return "";
}

/**
 * Pick the best list-of-paragraphs for the current language.
 *
 *  pickList(["a","b"], "en")                    -> ["a","b"]
 *  pickList({es:["a","b"],en:["x","y"]}, "en")  -> ["x","y"]
 *  pickList({es:["a"],en:[]}, "en")             -> ["a"]    (empty EN -> ES fallback)
 */
export function pickList(value, lang) {
  if (value == null) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== "object") return [];
  const target = Array.isArray(value[lang]) ? value[lang] : [];
  if (target.length > 0) return target;
  const other = lang === "es" ? "en" : "es";
  const otherVal = Array.isArray(value[other]) ? value[other] : [];
  return otherVal;
}

/** Ensure a value is always a bilingual object (used by editors). */
export function toBilingual(value) {
  if (value == null) return { es: "", en: "" };
  if (typeof value === "string") return { es: value, en: "" };
  if (typeof value !== "object") return { es: "", en: "" };
  return { es: value.es ?? "", en: value.en ?? "" };
}

/** Ensure a list value is always a bilingual list object (used by editors). */
export function toBilingualList(value) {
  if (value == null) return { es: [], en: [] };
  if (Array.isArray(value)) return { es: value, en: [] };
  if (typeof value !== "object") return { es: [], en: [] };
  return {
    es: Array.isArray(value.es) ? value.es : [],
    en: Array.isArray(value.en) ? value.en : [],
  };
}
